import {
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";

import { cn } from "@/lib/utils";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const openDialogStack: HTMLDivElement[] = [];

const getFocusableElements = (dialogRef: RefObject<HTMLDivElement | null>) =>
  Array.from(
    dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
  ).filter(
    (element) =>
      element.getClientRects().length > 0 &&
      !element.closest('[aria-hidden="true"]') &&
      !element.closest("[inert]"),
  );

type DialogAccessibleName =
  | { "aria-label": string; "aria-labelledby"?: never }
  | { "aria-label"?: never; "aria-labelledby": string };

type DialogProps = DialogAccessibleName & {
  open: boolean;
  onClose: () => void;
  /** Off by default so a stray click cannot discard edits in a settings
   *  dialog. */
  dismissOnBackdropClick?: boolean;
  className?: string;
  children: ReactNode;
};

/** Adds modal behavior and semantics without owning presentation. */
const Dialog = ({
  open,
  onClose,
  dismissOnBackdropClick = false,
  className,
  children,
  ...accessibleName
}: DialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  /* A drag that starts inside and ends on the backdrop must not close it. */
  const pressedBackdropRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    openerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const focusFrame = window.requestAnimationFrame(() => {
      dialogRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      const opener = openerRef.current;
      window.requestAnimationFrame(() => {
        if (opener?.isConnected) {
          opener.focus();
        }
      });
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }

    openDialogStack.push(dialog);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (openDialogStack[openDialogStack.length - 1] !== dialog) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusableElements(dialogRef);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (first === undefined || last === undefined) {
        event.preventDefault();
        dialogRef.current?.focus();
      } else if (!dialogRef.current?.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (activeElement === dialogRef.current) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      const stackIndex = openDialogStack.lastIndexOf(dialog);
      if (stackIndex !== -1) {
        openDialogStack.splice(stackIndex, 1);
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    // No body scroll-lock: toggling `overflow` on <body> breaks position:fixed
    // on iOS Safari — the dialog sticks to the locked layout viewport when you
    // pinch-zoom or pan (same fix as the SweepstakesModal on master).
    // `overscroll-contain` stops touch scroll from chaining to the page behind.
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
       jsx-a11y/no-noninteractive-element-interactions --
       Escape closes the dialog from a window listener the rule cannot see. */
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      className={cn("overscroll-contain", className)}
      onPointerDown={(event: ReactPointerEvent<HTMLDivElement>) => {
        pressedBackdropRef.current = event.target === event.currentTarget;
      }}
      onClick={(event: ReactMouseEvent<HTMLDivElement>) => {
        if (
          !dismissOnBackdropClick ||
          event.target !== event.currentTarget ||
          !pressedBackdropRef.current
        ) {
          return;
        }
        pressedBackdropRef.current = false;
        onCloseRef.current();
      }}
      {...accessibleName}
    >
      {children}
    </div>
  );
};

export default Dialog;
